// src/components/admin/dashboard/home/homeScreenData.js
//
// Read-only data layer for the Admin HomeScreen dashboard. Every section reuses
// the existing portal APIs — this file does NOT introduce new business logic,
// it only aggregates and shapes data for an at-a-glance morning overview.
//
// Each loader is independent and defensive: loadHomeScreenData() runs them with
// Promise.allSettled so one failing/permission-blocked source never blanks the
// whole page.

import { db } from "../../../../firebaseConfig";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  getDoc,
  doc,
  onSnapshot,
  documentId,
  Timestamp,
  writeBatch,
  serverTimestamp,
} from "firebase/firestore";

// --- Scheduling ---
import { fetchScheduledSessions } from "../../scheduling/schedulingApi";
import { subscribeToEventsForRange } from "../../scheduling/eventsApi";
import { computeEventsForDate } from "../../scheduling/eventUtils";
import { dateKeyLocal } from "../../scheduling/dayConfigUtils";
import { formatTimeFromMinutes } from "../../scheduling/timeUtils";

// --- Students / conferences ---
import { fetchStudents } from "../../student/studentFirestore";
import { parseStudentDate } from "../../student/studentUtils";

// --- Session logs ---
import { fetchSessionLogsBySessionIds } from "../../../tutor/session_logs/sessionLogApi";

// --- Diagnostics ---
import { buildDiagnosticReportUrl } from "../../diagnostics/diagnosticReportsData";
import { DIAGNOSTIC_LIVE_PROGRESS_COL } from "../../../student/diagnostics/diagnosticLiveProgress";

const DAY_MS = 24 * 60 * 60 * 1000;
const CONFERENCE_THRESHOLD = 25; // mirrors Student Directory's conference cycle

// ===================================================================
// Small shared helpers
// ===================================================================

export function startOfToday(now = new Date()) {
  const d = new Date(now);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function endOfDay(d) {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

export function fmtClock(value) {
  const d = value instanceof Date ? value : parseStudentDate(value);
  if (!d) return "—";
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

export function fmtDayLabel(d) {
  if (!(d instanceof Date)) return "—";
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

export function fmtSecondsLong(totalSeconds) {
  const s = Math.max(0, Math.round(Number(totalSeconds) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
}

// Minutes-only formatting (rounded up so a running clock never shows "0 min").
export function fmtMinutes(totalSeconds) {
  const s = Math.max(0, Number(totalSeconds) || 0);
  const mins = Math.ceil(s / 60);
  return `${mins} min`;
}

export function fmtTimeRange(start, end) {
  const a = start instanceof Date ? start : toDateMaybe(start);
  const b = end instanceof Date ? end : toDateMaybe(end);
  if (!a) return "—";
  const fmt = (d) =>
    d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }).replace(" ", "");
  if (!b) return fmt(a);
  return `${fmt(a)}–${fmt(b)}`;
}

function toDateMaybe(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value?.toDate === "function") {
    try {
      const d = value.toDate();
      return Number.isNaN(d.getTime()) ? null : d;
    } catch {
      return null;
    }
  }
  if (typeof value?.seconds === "number") return new Date(value.seconds * 1000);
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function normStatus(raw) {
  return String(raw || "").trim().toLowerCase();
}

// Resolve display names for a set of auth uids from the `users` collection.
async function resolveUserNames(uids = []) {
  const ids = [...new Set(uids.filter(Boolean))];
  const out = {};
  for (let i = 0; i < ids.length; i += 10) {
    const chunk = ids.slice(i, i + 10);
    try {
      const snap = await getDocs(
        query(collection(db, "users"), where(documentId(), "in", chunk))
      );
      snap.docs.forEach((d) => {
        const data = d.data() || {};
        out[d.id] = data.name || data.displayName || data.email || "";
      });
    } catch (err) {
      console.warn("resolveUserNames chunk failed", err);
    }
  }
  return out;
}

// ===================================================================
// Conference logic — mirrors buildConferenceMeta in StudentDirectory.js
// ===================================================================

function getLatestConferenceDate(student) {
  const list = Array.isArray(student?.conferences) ? student.conferences : [];
  let latest = null;
  list.forEach((c) => {
    const d = parseStudentDate(c?.date);
    if (d && (!latest || d.getTime() > latest.getTime())) latest = d;
  });
  return latest;
}

export function buildConferenceMeta(student) {
  const totalHoursRaw = Number(student?.totalSessionHours);
  const totalHours = Number.isFinite(totalHoursRaw) ? Math.max(0, totalHoursRaw) : 0;

  const latestConferenceDate = getLatestConferenceDate(student);
  const conferenceBaselineDate =
    parseStudentDate(student?.conferenceLastCompletedAt) || latestConferenceDate;
  const conferenceBaselineHoursRaw = Number(student?.conferenceLastCompletedHours);
  const conferenceBaselineHours =
    Number.isFinite(conferenceBaselineHoursRaw) && conferenceBaselineHoursRaw >= 0
      ? conferenceBaselineHoursRaw
      : 0;

  const cycleResetDate = parseStudentDate(student?.conferenceCycleResetAt);
  const resetHoursRaw = Number(student?.conferenceCycleResetHours);
  const hasResetHours = Number.isFinite(resetHoursRaw) && resetHoursRaw >= 0;

  let baselineDate = conferenceBaselineDate;
  let baselineHours = conferenceBaselineHours;
  if (cycleResetDate && (!baselineDate || cycleResetDate.getTime() > baselineDate.getTime())) {
    baselineDate = cycleResetDate;
    baselineHours = hasResetHours ? resetHoursRaw : totalHours;
  }

  const hoursSinceBaseline = Math.max(0, totalHours - baselineHours);
  const conferenceNeeded = hoursSinceBaseline >= CONFERENCE_THRESHOLD;

  return {
    latestConferenceDate,
    totalHours,
    hoursSinceBaseline,
    threshold: CONFERENCE_THRESHOLD,
    conferenceNeeded,
  };
}

const INACTIVE_STUDENT_STATUSES = new Set(["finished", "no answer", "not interested"]);

// ===================================================================
// Section loaders
// ===================================================================

// --- Upcoming Schedule / Events ---
async function loadEventsForRange(rangeStart, rangeEnd) {
  // subscribeToEventsForRange is an onSnapshot listener; resolve on first emit
  // then immediately unsubscribe so this stays a one-shot read.
  return new Promise((resolve) => {
    let settled = false;
    let unsub = () => {};
    const done = (events) => {
      if (settled) return;
      settled = true;
      try {
        unsub();
      } catch {
        /* noop */
      }
      resolve(Array.isArray(events) ? events : []);
    };
    try {
      unsub = subscribeToEventsForRange(rangeStart, rangeEnd, done, () => done([]));
    } catch {
      resolve([]);
    }
    // Safety: never hang the dashboard on a slow listener.
    setTimeout(() => done([]), 6000);
  });
}

// Scheduling-page colors, reused so admins can distinguish items at a glance.
// Session backgrounds match AdminScheduling.css; events match EventCard (#009EEB).
const SESSION_COLORS = {
  pending: { tint: "#FFFEF0", accent: "#E0B400" },
  confirmed: { tint: "#F1FCEA", accent: "#34A853" },
  present: { tint: "#EDF6FF", accent: "#4285F4" },
  canceled: { tint: "#FFF1F1", accent: "#EA4335" },
  no_show: { tint: "#F4F4F4", accent: "#9AA0A6" },
};
const EVENT_COLOR = { tint: "#E6F6FE", accent: "#009EEB" };

export function scheduleItemColor(item) {
  if (!item || item.kind === "event") return EVENT_COLOR;
  const s = normStatus(item.status).replace(/\s|-/g, "_");
  return SESSION_COLORS[s] || SESSION_COLORS.pending;
}

export async function loadUpcomingSchedule({ daysAhead = 7, now = new Date() } = {}) {
  const todayStart = startOfToday(now);
  const todayKey = dateKeyLocal(todayStart);
  const rangeEnd = endOfDay(new Date(todayStart.getTime() + daysAhead * DAY_MS));
  const nowMs = now.getTime();

  const [sessions, rawEvents] = await Promise.all([
    fetchScheduledSessions({ rangeStart: todayStart, rangeEnd, includeDeleted: false }),
    loadEventsForRange(todayStart, rangeEnd),
  ]);

  const sessionItems = (sessions || [])
    .filter((s) => normStatus(s.status) !== "canceled")
    .map((s) => ({
      kind: "session",
      id: s.id,
      studentId: s.studentId || "",
      start: s.start instanceof Date ? s.start : toDateMaybe(s.start),
      end: s.end instanceof Date ? s.end : toDateMaybe(s.end),
      studentName: s.studentName || "Unknown student",
      tutorName: s.tutorName || "—",
      subject: s.subject || "",
      status: s.status || "Pending",
    }))
    .filter((s) => s.start);

  // Expand events (incl. recurrence) per day across the range.
  const eventItems = [];
  for (let i = 0; i <= daysAhead; i += 1) {
    const day = new Date(todayStart.getTime() + i * DAY_MS);
    const key = dateKeyLocal(day);
    const occurrences = computeEventsForDate(rawEvents, key) || [];
    occurrences.forEach((ev) => {
      const start = new Date(day);
      const end = new Date(day);
      const hasTime = typeof ev.startTime === "number";
      if (hasTime) {
        start.setHours(0, 0, 0, 0);
        start.setMinutes(ev.startTime);
      }
      if (typeof ev.endTime === "number") {
        end.setHours(0, 0, 0, 0);
        end.setMinutes(ev.endTime);
      }
      eventItems.push({
        kind: "event",
        id: `${ev.id}__${key}`,
        start,
        end: typeof ev.endTime === "number" ? end : null,
        allDay: !hasTime,
        timeLabel: hasTime
          ? typeof ev.endTime === "number"
            ? `${formatTimeFromMinutes(ev.startTime)} – ${formatTimeFromMinutes(ev.endTime)}`
            : formatTimeFromMinutes(ev.startTime)
          : "All day",
        title: ev.title || "Event",
      });
    });
  }

  // Counts use ALL of today's items; the displayed list shows only items that
  // are still happening or upcoming — an item disappears once it has ended
  // (we compare against its END time, so in-progress sessions stay visible).
  const todaySessions = sessionItems.filter((s) => dateKeyLocal(s.start) === todayKey).length;
  const todayEvents = eventItems.filter((e) => dateKeyLocal(e.start) === todayKey).length;

  const notEnded = (it) => {
    const ref = it.end instanceof Date ? it.end : it.start;
    return ref && ref.getTime() >= nowMs;
  };
  const items = [...sessionItems, ...eventItems]
    .filter(notEnded)
    .sort((a, b) => a.start.getTime() - b.start.getTime());

  return {
    items,
    todayKey,
    todayCount: todaySessions,
    eventsToday: todayEvents,
    total: items.length,
  };
}

// --- Conference Needed + New Students share one students fetch ---
export async function loadStudentSections({ now = new Date(), newWithinDays = 14 } = {}) {
  const students = await fetchStudents();
  const list = Array.isArray(students) ? students : [];

  // Conference Needed
  const conference = [];
  list.forEach((student) => {
    const status = normStatus(student?.status);
    if (INACTIVE_STUDENT_STATUSES.has(status)) return;
    const totalHours = Number(student?.totalSessionHours) || 0;
    if (totalHours <= 0) return;
    const meta = buildConferenceMeta(student);
    if (!meta.conferenceNeeded) return;
    conference.push({
      id: student.id,
      name: student.name || "Unknown",
      grade: student.grade ?? "",
      status: student.status || "",
      hoursSinceBaseline: meta.hoursSinceBaseline,
      totalHours: meta.totalHours,
      threshold: meta.threshold,
      latestConferenceDate: meta.latestConferenceDate,
    });
  });
  conference.sort((a, b) => b.hoursSinceBaseline - a.hoursSinceBaseline);

  // New Students to Follow Up
  const cutoff = new Date(now.getTime() - newWithinDays * DAY_MS);
  const newStudents = [];
  list.forEach((student) => {
    const created = parseStudentDate(student?.createdAt) || parseStudentDate(student?.startDate);
    const status = normStatus(student?.status);
    const isRecent = created && created.getTime() >= cutoff.getTime();
    const isSignedUp = status === "signed up";
    if (!isRecent && !isSignedUp) return;
    if (INACTIVE_STUDENT_STATUSES.has(status)) return;
    // Once an admin has opened this student's detail view it's considered
    // reviewed and no longer needs follow-up (it stays in the Directory).
    if (student?.followUpReviewedAt) return;
    newStudents.push({
      id: student.id,
      name: student.name || "Unknown",
      grade: student.grade ?? "",
      status: student.status || "Signed Up",
      signupDate: created,
      totalHours: Number(student?.totalSessionHours) || 0,
    });
  });
  newStudents.sort((a, b) => {
    const at = a.signupDate ? a.signupDate.getTime() : 0;
    const bt = b.signupDate ? b.signupDate.getTime() : 0;
    return bt - at;
  });

  return { conference, newStudents };
}

// --- Diagnostic Test Progress ---
//
// Subject groupings let us present a high-level summary (e.g. "Reading & Writing
// 87%, Math 0%") before the per-module detail. SAT/PSAT have two subjects of two
// modules each; ACT has four single-section subjects.
const SUBJECT_PLANS = {
  SAT: [
    { key: "rw", label: "Reading & Writing", stages: ["rw1", "rw2"] },
    { key: "math", label: "Math", stages: ["m1", "m2"] },
  ],
  PSAT: [
    { key: "rw", label: "Reading & Writing", stages: ["rw1", "rw2"] },
    { key: "math", label: "Math", stages: ["m1", "m2"] },
  ],
  ACT: [
    { key: "english", label: "English", stages: ["english"] },
    { key: "math", label: "Math", stages: ["math"] },
    { key: "reading", label: "Reading", stages: ["reading"] },
    { key: "science", label: "Science", stages: ["science"] },
  ],
};

function clampPct(n) {
  return Math.max(0, Math.min(100, Math.round(Number(n) || 0)));
}

function computeDiagnosticSubjects(testType, doc) {
  const type = String(testType || "SAT").toUpperCase();
  const plan = SUBJECT_PLANS[type] || SUBJECT_PLANS.SAT;
  const completedKeys = new Set(
    (Array.isArray(doc.completedSections) ? doc.completedSections : []).map((c) => c.key)
  );
  const currentStage = doc.currentStageKey || "";
  const total = Number(doc.totalQuestions) || 0;
  const answered = Number(doc.questionsAnswered) || 0;
  const frac = total > 0 ? Math.min(1, answered / total) : 0;

  const subjects = plan.map((subj) => {
    const modules = subj.stages.length;
    const done = subj.stages.filter((s) => completedKeys.has(s)).length;
    const currentHere = subj.stages.includes(currentStage);
    const pct = clampPct((done / modules) * 100 + (currentHere ? frac * (100 / modules) : 0));
    let status = "not_started";
    if (done >= modules) status = "completed";
    else if (currentHere || done > 0) status = "in_progress";
    return { key: subj.key, label: subj.label, pct, status };
  });

  const overallPct = clampPct(
    subjects.reduce((sum, s) => sum + s.pct, 0) / (subjects.length || 1)
  );
  return { subjects, overallPct };
}

function buildDiagnosticView(docs, { now = new Date(), activeWithinMinutes = 45, nameMap = {} } = {}) {
  const activeCutoff = now.getTime() - activeWithinMinutes * 60 * 1000;
  const completedCutoff = now.getTime() - 3 * DAY_MS; // recently completed = last 3 days
  const nameFor = (d) => d.studentName || nameMap[d.studentId] || "Student";

  const active = [];
  const completed = [];

  docs.forEach((d) => {
    const updated = toDateMaybe(d.updatedAt);
    if (d.status === "completed") {
      const completedAt = toDateMaybe(d.completedAt) || updated;
      if (completedAt && completedAt.getTime() >= completedCutoff) {
        completed.push({
          id: d.id,
          studentName: nameFor(d),
          testType: d.testType || "SAT",
          completedAt,
          scoreSummary: d.scoreSummary || null,
          reportUrl: buildDiagnosticReportUrl({
            studentId: d.studentId,
            attemptId: d.attemptId || d.id,
            type: d.testType,
          }),
        });
      }
      return;
    }
    // in_progress
    if (!updated || updated.getTime() < activeCutoff) return; // stale / abandoned
    const total = Number(d.totalQuestions) || 0;
    const answered = Number(d.questionsAnswered) || 0;
    const { subjects, overallPct } = computeDiagnosticSubjects(d.testType, d);
    active.push({
      id: d.id,
      studentName: nameFor(d),
      testType: d.testType || "SAT",
      currentSectionLabel: d.currentSectionLabel || "—",
      currentStageKey: d.currentStageKey || "",
      isBreak: !!d.isBreak,
      questionsAnswered: answered,
      totalQuestions: total,
      questionsLeft: Math.max(0, total - answered),
      sectionTimeLeftSeconds: Number(d.sectionTimeLeftSeconds) || 0,
      estimatedTotalTimeLeftSeconds: Number(d.estimatedTotalTimeLeftSeconds) || 0,
      subjects,
      overallPct,
      lastUpdated: updated,
    });
  });

  active.sort((a, b) => b.lastUpdated.getTime() - a.lastUpdated.getTime());
  completed.sort((a, b) => b.completedAt.getTime() - a.completedAt.getTime());
  return { active, completed };
}

// Module-level cache so live updates don't re-fetch names on every snapshot.
const _diagNameCache = {};

export async function loadDiagnosticProgress({ now = new Date(), activeWithinMinutes = 45 } = {}) {
  let docs = [];
  try {
    const snap = await getDocs(
      query(collection(db, DIAGNOSTIC_LIVE_PROGRESS_COL), orderBy("updatedAt", "desc"), limit(100))
    );
    docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.warn("loadDiagnosticProgress failed", err);
    return { active: [], completed: [] };
  }
  const needNames = docs.filter((d) => !d.studentName && !_diagNameCache[d.studentId]).map((d) => d.studentId);
  if (needNames.length) Object.assign(_diagNameCache, await resolveUserNames(needNames));
  return buildDiagnosticView(docs, { now, activeWithinMinutes, nameMap: _diagNameCache });
}

/**
 * Real-time subscription to diagnostic progress. Calls onData({active, completed})
 * immediately and on every change so the dashboard updates live as students move
 * through their tests. Returns an unsubscribe function.
 */
export function subscribeDiagnosticProgress(onData, onError) {
  const qy = query(
    collection(db, DIAGNOSTIC_LIVE_PROGRESS_COL),
    orderBy("updatedAt", "desc"),
    limit(100)
  );
  let lastDocs = [];
  const emit = () =>
    onData(buildDiagnosticView(lastDocs, { now: new Date(), nameMap: _diagNameCache }));

  return onSnapshot(
    qy,
    async (snap) => {
      lastDocs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      emit(); // paint immediately with whatever names we have
      const missing = lastDocs
        .filter((d) => !d.studentName && !_diagNameCache[d.studentId])
        .map((d) => d.studentId);
      if (missing.length) {
        Object.assign(_diagNameCache, await resolveUserNames(missing));
        emit(); // repaint with resolved names
      }
    },
    (err) => {
      console.warn("subscribeDiagnosticProgress failed", err);
      if (onError) onError(err);
    }
  );
}

// --- Live Clock In / Clock Out + Abnormal Clock Out ---
export async function loadClockStatus({ now = new Date(), lookbackDays = 7 } = {}) {
  const cutoff = new Date(now.getTime() - lookbackDays * DAY_MS);

  const [openSnap, activeSnap, payrollSnap] = await Promise.all([
    getDocs(query(collection(db, "open_clock_sessions"), where("status", "==", "open"))).catch(
      () => null
    ),
    getDocs(collection(db, "active_sessions")).catch(() => null),
    getDocs(
      query(
        collection(db, "payroll_records"),
        where("startTime", ">=", cutoff),
        orderBy("startTime", "desc")
      )
    ).catch(() => null),
  ]);

  // Currently clocked in: kiosk tutors (open_clock_sessions) + admins (active_sessions).
  // A single active clock-in is often written to BOTH collections (the kiosk's
  // open session AND a live active_sessions mirror), so the same tutor would
  // otherwise appear twice. Deduplicate by uid — one row per tutor — preferring
  // the kiosk (open_clock_sessions) record since it's the authoritative session.
  // Rows without a uid can't be deduped meaningfully, so they're always kept.
  const clockedIn = [];
  const seenUid = new Set();
  const addClockedIn = (entry) => {
    if (entry.uid) {
      if (seenUid.has(entry.uid)) return; // already represented (kiosk wins; added first)
      seenUid.add(entry.uid);
    }
    clockedIn.push(entry);
  };
  if (openSnap) {
    openSnap.docs.forEach((d) => {
      const data = d.data() || {};
      addClockedIn({
        id: d.id,
        uid: data.uid || "",
        name: data.userName || "Tutor",
        since: toDateMaybe(data.clockInAt),
        source: "kiosk",
      });
    });
  }
  if (activeSnap) {
    activeSnap.docs.forEach((d) => {
      const data = d.data() || {};
      addClockedIn({
        id: d.id,
        uid: data.uid || "",
        name: data.name || "Admin",
        since: toDateMaybe(data.startTime),
        source: "admin",
      });
    });
  }
  clockedIn.sort((a, b) => (b.since?.getTime() || 0) - (a.since?.getTime() || 0));

  const payroll = payrollSnap ? payrollSnap.docs.map((d) => ({ id: d.id, ...d.data() })) : [];

  // A clock-out is "auto" ONLY when it landed exactly at 12:00 AM — that's the
  // single abnormal case (the kiosk's midnight safety close). Other automatic
  // bookkeeping is NOT flagged.
  const isMidnightClockOut = (clockOut) =>
    !!clockOut && clockOut.getHours() === 0 && clockOut.getMinutes() === 0;

  const shifts = payroll
    .map((r) => {
      const clockIn = toDateMaybe(r.startTime);
      const clockOut = toDateMaybe(r.endTime);
      return {
        id: r.id,
        uid: r.uid || "",
        name: r.tutorName || "Tutor",
        clockIn,
        clockOut,
        clockSessionId: r.clockSessionId || "",
        dateStr: r.date || "",
        // Once an admin has manually corrected a midnight auto clock-out, it's a
        // normal corrected shift — never flag it as "Auto" again.
        isAuto: isMidnightClockOut(clockOut) && r.autoCloseCorrected !== true,
      };
    })
    .filter((s) => s.clockIn)
    .sort((a, b) => b.clockIn.getTime() - a.clockIn.getTime());

  const abnormal = shifts
    .filter((s) => s.isAuto)
    .map((s) => ({
      id: s.id,
      uid: s.uid,
      name: s.name,
      date: s.clockIn,
      clockIn: s.clockIn,
      clockOut: s.clockOut,
      clockSessionId: s.clockSessionId,
      dateStr: s.dateStr,
    }))
    .slice(0, 12);

  return { clockedIn, shifts, abnormal };
}

// --- Correct a midnight Auto Clock-Out ---
//
// When the kiosk's midnight-safety job auto-closes a forgotten shift, it writes
// the clock-out at 12:00 AM and may split the shift into several payroll_records
// (admin/teaching segments) that all share one `clockSessionId`. An admin can
// fix the real clock-out time from the dashboard; this re-shapes the WHOLE shift
// to the new end:
//   • segments that start at/after the new clock-out are removed entirely,
//   • the segment that straddles the new clock-out is truncated to it,
//   • each remaining segment's hours / totalPay are recomputed from its rate.
// Because we only ever SHRINK the shift end, the admin/teaching boundaries inside
// [clockIn, newClockOut] are unchanged — so no schedule re-lookup is needed and
// the result matches what the backend split would have produced for that end.
// The open_clock_sessions mirror is updated too so payroll/clock history agree,
// and the auto-close flags are cleared so the row stops showing as "Auto".
function round2(value) {
  return Math.round((Number(value) + Number.EPSILON) * 100) / 100;
}

export async function correctAutoClockOut({ recordId, clockSessionId, newClockOut }) {
  if (!(newClockOut instanceof Date) || Number.isNaN(newClockOut.getTime())) {
    throw new Error("Please enter a valid clock-out time.");
  }
  const newEndMs = newClockOut.getTime();

  // Collect every payroll segment for this shift. Prefer the clockSessionId so
  // multi-segment shifts stay consistent; fall back to the single flagged record.
  let segments = [];
  if (clockSessionId) {
    const snap = await getDocs(
      query(collection(db, "payroll_records"), where("clockSessionId", "==", clockSessionId))
    ).catch(() => null);
    if (snap) segments = snap.docs.map((d) => ({ ref: d.ref, data: d.data() || {} }));
  }
  if (!segments.length && recordId) {
    const single = await getDoc(doc(db, "payroll_records", recordId)).catch(() => null);
    if (single && single.exists()) segments = [{ ref: single.ref, data: single.data() || {} }];
  }
  if (!segments.length) {
    throw new Error("Could not find the payroll record to correct.");
  }

  // The new clock-out must be after the shift actually started.
  const starts = segments
    .map((s) => toDateMaybe(s.data.startTime))
    .filter(Boolean)
    .map((d) => d.getTime());
  const shiftStartMs = starts.length ? Math.min(...starts) : null;
  if (shiftStartMs != null && newEndMs <= shiftStartMs) {
    throw new Error("The clock-out time must be after the clock-in time.");
  }

  const batch = writeBatch(db);
  let totalHours = 0;
  let totalPay = 0;

  segments.forEach(({ ref, data }) => {
    const segStart = toDateMaybe(data.startTime);
    const segEnd = toDateMaybe(data.endTime);
    if (!segStart) return;

    // Segment begins at/after the corrected clock-out → it never happened.
    if (segStart.getTime() >= newEndMs) {
      batch.delete(ref);
      return;
    }

    // Otherwise keep it, truncating its end to the corrected clock-out if needed.
    const boundedEnd = segEnd && segEnd.getTime() <= newEndMs ? segEnd : newClockOut;
    const hours = round2((boundedEnd.getTime() - segStart.getTime()) / 3600000);
    const rate = Number(data.rateUsed || 0);
    const pay = round2(hours * rate);
    totalHours += hours;
    totalPay += pay;

    batch.update(ref, {
      endTime: Timestamp.fromDate(boundedEnd),
      hours,
      totalPay: pay,
      autoClosed: false,
      autoCloseCorrected: true,
      autoCloseReason: "corrected_manually",
      correctedClockOutAt: Timestamp.fromDate(newClockOut),
      updatedAt: serverTimestamp(),
    });
  });

  // Keep the clock session (clock history) in step with the corrected payroll.
  if (clockSessionId) {
    batch.set(
      doc(db, "open_clock_sessions", clockSessionId),
      {
        clockOutAt: Timestamp.fromDate(newClockOut),
        totalHours: round2(totalHours),
        totalPay: round2(totalPay),
        autoClosed: false,
        autoCloseCorrected: true,
        autoCloseReason: "corrected_manually",
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  }

  await batch.commit();
  return { totalHours: round2(totalHours), totalPay: round2(totalPay) };
}

// --- Missing Session Logs (last 14 days only) ---
export async function loadMissingSessionLogs({ now = new Date(), lookbackDays = 14 } = {}) {
  const rangeStart = new Date(now.getTime() - lookbackDays * DAY_MS);
  const rangeEnd = now;

  const sessions = await fetchScheduledSessions({
    rangeStart,
    rangeEnd,
    includeDeleted: false,
  });

  // Only sessions that have already started and weren't canceled / no-show need a log.
  const candidates = (sessions || []).filter((s) => {
    const start = s.start instanceof Date ? s.start : toDateMaybe(s.start);
    if (!start || start.getTime() > now.getTime()) return false;
    const st = normStatus(s.status);
    return st !== "canceled" && st !== "no_show" && st !== "no show";
  });

  if (!candidates.length) return { missing: [], checked: 0 };

  const ids = candidates.map((s) => s.id);
  // fetchSessionLogsBySessionIds already filters out drafts (isDraft === true),
  // so any candidate without an entry here has no SUBMITTED log.
  const logs = await fetchSessionLogsBySessionIds(ids);

  const missing = candidates
    .filter((s) => !logs[s.id])
    .map((s) => ({
      id: s.id,
      studentId: s.studentId || "",
      start: s.start instanceof Date ? s.start : toDateMaybe(s.start),
      end: s.end instanceof Date ? s.end : toDateMaybe(s.end),
      studentName: s.studentName || "Unknown student",
      tutorName: s.tutorName || "—",
      subject: s.subject || "",
      status: s.status || "",
    }))
    .filter((s) => s.start)
    .sort((a, b) => b.start.getTime() - a.start.getTime());

  return { missing, checked: candidates.length };
}

// ===================================================================
// Top-level aggregate loader
// ===================================================================

export async function loadHomeScreenData({ now = new Date() } = {}) {
  const [schedule, students, diagnostics, clock, missingLogs] = await Promise.allSettled([
    loadUpcomingSchedule({ now }),
    loadStudentSections({ now }),
    loadDiagnosticProgress({ now }),
    loadClockStatus({ now }),
    loadMissingSessionLogs({ now }),
  ]);

  const val = (settled, fallback) =>
    settled.status === "fulfilled" ? settled.value : fallback;

  const studentsVal = val(students, { conference: [], newStudents: [] });

  return {
    schedule: val(schedule, { items: [], todayKey: "", todayCount: 0, eventsToday: 0, total: 0 }),
    conference: studentsVal.conference,
    newStudents: studentsVal.newStudents,
    diagnostics: val(diagnostics, { active: [], completed: [] }),
    clock: val(clock, { clockedIn: [], shifts: [], abnormal: [] }),
    missingLogs: val(missingLogs, { missing: [], checked: 0 }),
    errors: {
      schedule: schedule.status === "rejected",
      students: students.status === "rejected",
      diagnostics: diagnostics.status === "rejected",
      clock: clock.status === "rejected",
      missingLogs: missingLogs.status === "rejected",
    },
  };
}

// ===================================================================
// Attention counts — lightweight aggregate for the Home sidebar badge.
//
// Counts exactly what the Home dashboard surfaces as needing admin action:
//   • new (unreviewed) sign-up students — the "New Students to Follow Up" list
//   • missing session logs + midnight auto clock-outs — the
//     "Missing & Needs Attention" list
// Each source is independent (Promise.allSettled) so one failure never blanks
// the badge. Returns a breakdown plus the `total` shown on the sidebar.
// ===================================================================
export async function loadAttentionCounts({ now = new Date() } = {}) {
  const [students, missingLogs, clock] = await Promise.allSettled([
    loadStudentSections({ now }),
    loadMissingSessionLogs({ now }),
    loadClockStatus({ now }),
  ]);

  const newStudents =
    students.status === "fulfilled" ? students.value.newStudents.length : 0;
  const missingLogsCount =
    missingLogs.status === "fulfilled" ? missingLogs.value.missing.length : 0;
  const abnormalClockOuts =
    clock.status === "fulfilled" ? clock.value.abnormal.length : 0;

  const needsAttention = missingLogsCount + abnormalClockOuts;

  return {
    newStudents,
    missingLogs: missingLogsCount,
    abnormalClockOuts,
    needsAttention,
    total: newStudents + needsAttention,
  };
}
